import { auth } from '../config/firebase';
import { getIdToken } from 'firebase/auth';
import { base_url } from '../utils/network';
import authService from './authService';

// Token cache to avoid generating tokens for every request
let tokenCache = {
  token: null,
  expiry: null,
  user: null
};

// Load token cache from localStorage on initialization
const loadTokenCache = () => {
  try {
    const cached = localStorage.getItem('firebase_token_cache');
    if (cached) {
      const parsed = JSON.parse(cached);
      // Check if cache is still valid (not expired)
      if (parsed.expiry && Date.now() < parsed.expiry) {
        tokenCache = parsed;
        console.log('AuthenticatedApiService: Loaded token from localStorage cache');
      } else {
        console.log('AuthenticatedApiService: Cached token expired, clearing cache');
        localStorage.removeItem('firebase_token_cache');
      }
    }
  } catch (error) {
    console.log('AuthenticatedApiService: Error loading token cache:', error);
    localStorage.removeItem('firebase_token_cache');
  }
};

// Save token cache to localStorage
const saveTokenCache = () => {
  try {
    localStorage.setItem('firebase_token_cache', JSON.stringify(tokenCache));
  } catch (error) {
    console.log('AuthenticatedApiService: Error saving token cache:', error);
  }
};

// Clear token cache from localStorage
const clearTokenCache = () => {
  tokenCache = { token: null, expiry: null, user: null };
  localStorage.removeItem('firebase_token_cache');
};

// Initialize token cache on module load
loadTokenCache();

// Wait for Firebase Auth to be ready
const waitForAuth = async () => {
  return new Promise((resolve) => {
    const checkAuth = () => {
      if (auth.currentUser) {
        resolve();
      } else {
        // Wait a bit and check again
        setTimeout(checkAuth, 100);
      }
    };
    
    // Initial check
    checkAuth();
    
    // Fallback timeout
    setTimeout(() => {
      resolve();
    }, 3000);
  });
};

// Get cached token or generate new one if needed
const getValidToken = async () => {
  const currentUser = auth.currentUser;
  
  // If user changed, clear cache
  if (tokenCache.user !== currentUser?.uid) {
    clearTokenCache();
    tokenCache.user = currentUser?.uid;
  }
  
  // If no cached token or token is expired, get new one
  if (!tokenCache.token || !tokenCache.expiry || Date.now() > tokenCache.expiry) {
    console.log('AuthenticatedApiService: Generating fresh token');
    tokenCache.token = await getIdToken(currentUser);
    // Set expiry to 50 minutes (tokens are valid for 1 hour, refresh 10 mins early)
    tokenCache.expiry = Date.now() + (50 * 60 * 1000);
    tokenCache.user = currentUser?.uid;
    // Save to localStorage
    saveTokenCache();
  } else {
    console.log('AuthenticatedApiService: Using cached token');
  }
  
  return tokenCache.token;
};

// Make authenticated request using Firebase ID token
const makeRequest = async (endpoint, options = {}) => {
  console.log('AuthenticatedApiService: Making request to:', endpoint);
  
  // Check if user is authenticated via auth service
  const isAuth = authService.isAuthenticated();
  console.log('AuthenticatedApiService: Auth service authenticated:', isAuth);
  
  if (!isAuth) {
    throw new Error('User not authenticated');
  }

  // Wait for Firebase Auth to be ready and get current user
  console.log('AuthenticatedApiService: Waiting for Firebase Auth...');
  await waitForAuth();
  const currentUser = auth.currentUser;
  console.log('AuthenticatedApiService: Firebase user:', !!currentUser);
  
  if (!currentUser) {
    throw new Error('Firebase user not available');
  }

  // Get valid token (cached or fresh)
  const idToken = await getValidToken();
  console.log('AuthenticatedApiService: Using token, length:', idToken.length);
  
  const url = `${base_url}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    }
  });

  if (response.status === 401) {
    // Token expired, clear cache and try once more
    console.log('AuthenticatedApiService: Token expired, clearing cache and retrying');
    clearTokenCache();
    const freshToken = await getIdToken(currentUser);
    
    const retryResponse = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${freshToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (retryResponse.status === 401) {
      throw new Error('Session expired. Please sign in again.');
    }
    
    return retryResponse;
  }

  return response;
};

// Bookmarks API
export const getBookmarks = async () => {
  const response = await makeRequest('bookmarks');
  return response.json();
};

export const createBookmark = async (bookmarkData) => {
  const response = await makeRequest('bookmarks', {
    method: 'POST',
    body: JSON.stringify(bookmarkData)
  });
  return response.json();
};

export const updateBookmark = async (bookmarkData) => {
  const response = await makeRequest('bookmarks', {
    method: 'PUT',
    body: JSON.stringify(bookmarkData)
  });
  return response.json();
};

export const deleteBookmark = async (groupId, bookmarkId) => {
  const response = await makeRequest(`bookmarks?g_id=${groupId}&id=${bookmarkId}`, {
    method: 'DELETE'
  });
  return response.json();
};

export const moveBookmark = async (fromGroupId, toGroupId, bookmarkId, position) => {
  const response = await makeRequest(`bookmarks?fromGroupId=${fromGroupId}&toGroupId=${toGroupId}&bookmarkId=${bookmarkId}&position=${position}`, {
    method: 'PATCH'
  });
  return response.json();
};

// Groups API
export const createGroup = async (name) => {
  const response = await makeRequest('groups', {
    method: 'POST',
    body: JSON.stringify({ name })
  });
  return response.json();
};

export const updateGroup = async (groupData) => {
  const response = await makeRequest('groups', {
    method: 'PUT',
    body: JSON.stringify(groupData)
  });
  return response.json();
};

export const deleteGroup = async (groupId) => {
  const response = await makeRequest(`groups?groupId=${groupId}`, {
    method: 'DELETE'
  });
  return response.json();
};

// User API
export const getUserProfile = async () => {
  const response = await makeRequest('auth/profile');
  return response.json();
};

// Legacy object export for backward compatibility
export const authenticatedApiService = {
  getBookmarks,
  createBookmark,
  updateBookmark,
  deleteBookmark,
  moveBookmark,
  createGroup,
  updateGroup,
  deleteGroup,
  getUserProfile
};
